import React, { useEffect, useRef, useState } from 'react';
import IngestionUploadForm from './IngestionUploadForm';
import IngestionJobList from './IngestionJobList';
import ParsedDraftsGrid from './ParsedDraftsGrid';
import { VocabularyReview } from '../vocabulary/VocabularyManagement';

// /admin/ingest shell. Three tabs:
//   - Ingest: upload form + jobs list
//   - Drafts: per-batch review (auto-flips here after a job completes)
//   - All Yet To Review: global pending pile, status locked to PENDING_REVIEW
// All three share state through this component; the right-tab grids share the
// same ParsedDraftsGrid component via different props.
const IngestionPage = () => {
    const [activeTab, setActiveTab] = useState('ingest');
    const [jobFilter, setJobFilter] = useState(null);
    const [refreshJobs, setRefreshJobs] = useState(0);
    const [recentApprovals, setRecentApprovals] = useState([]); // [{draft_id, question_id, at}]
    const fadeTimer = useRef(null);

    const handleJobComplete = (job) => {
        // Refresh the jobs grid so the new CSV job row appears at once, then flip
        // to Drafts so the parsed rows are ready to review.
        setRefreshJobs(n => n + 1);
        if (job.status === 'DONE') {
            setJobFilter(job.job_id);
            setActiveTab('drafts');
        }
    };

    const handleSelectJob = (jobId) => {
        setJobFilter(jobId);
        setActiveTab('drafts');
    };

    // If the job currently filtering the Drafts tab was removed, clear the filter.
    const handleJobRemoved = (jobId) => {
        setJobFilter(prev => (prev === jobId ? null : prev));
    };

    // Track recent approvals so the admin sees a running success ticker without
    // leaving the page. Auto-clears after 8s.
    const handleApproved = (draftId, questionId) => {
        setRecentApprovals(prev => [
            { draft_id: draftId, question_id: questionId, at: Date.now() },
            ...prev,
        ].slice(0, 5));
        if (fadeTimer.current) clearTimeout(fadeTimer.current);
        fadeTimer.current = setTimeout(() => setRecentApprovals([]), 8000);
    };

    useEffect(() => () => { if (fadeTimer.current) clearTimeout(fadeTimer.current); }, []);

    return (
        <div className="container-fluid py-4 px-4">
            <h2 className="mb-4">Add Questions To Question Bank</h2>

            {recentApprovals.length > 0 && (
                <div className="alert alert-success py-2 mb-3 d-flex align-items-center">
                    <span className="me-2">✓ Stored</span>
                    {recentApprovals.map((r, i) => (
                        <span key={`${r.draft_id}-${r.at}`} className="me-2">
                            {i > 0 && <span className="text-muted mx-1">·</span>}
                            draft #{r.draft_id} → question #{r.question_id}
                        </span>
                    ))}
                </div>
            )}

            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${activeTab === 'ingest' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ingest')}
                    >Ingest</button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${activeTab === 'drafts' ? 'active' : ''}`}
                        onClick={() => setActiveTab('drafts')}
                    >Drafts</button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${activeTab === 'all-yet-to-review' ? 'active' : ''}`}
                        onClick={() => setActiveTab('all-yet-to-review')}
                        title="All pending drafts across every job"
                    >All Yet To Review</button>
                </li>
                <li className="nav-item">
                    <button
                        type="button"
                        className={`nav-link ${activeTab === 'vocabulary' ? 'active' : ''}`}
                        onClick={() => setActiveTab('vocabulary')}
                        title="Review, edit and approve pushed vocabulary words + practice MCQs"
                    >Vocabulary</button>
                </li>
            </ul>

            {activeTab === 'ingest' && (
                <>
                    <IngestionUploadForm onJobComplete={handleJobComplete} />
                    <IngestionJobList
                        refreshSignal={refreshJobs}
                        onSelectJob={handleSelectJob}
                        onJobRemoved={handleJobRemoved}
                    />
                </>
            )}

            {activeTab === 'drafts' && (
                <ParsedDraftsGrid
                    jobIdFilter={jobFilter}
                    onClearJobFilter={() => setJobFilter(null)}
                    onApproved={handleApproved}
                />
            )}

            {activeTab === 'all-yet-to-review' && (
                <ParsedDraftsGrid
                    jobIdFilter={null}
                    reviewMode={true}
                    onApproved={handleApproved}
                />
            )}

            {activeTab === 'vocabulary' && <VocabularyReview />}
        </div>
    );
};

export default IngestionPage;
